import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouteError } from './components/shell/RouteError';
import {
  ROUTE_PENDING_MIN_MS,
  ROUTE_PENDING_MS,
  RoutePending,
} from './components/shell/RoutePending';
import { isUnauthenticated, shouldRetryRequest } from './lib/api';
import { installServiceWorker } from './lib/service-worker';
import { SIGN_IN_PATH, isSessionQuery, signInHref } from './lib/session';
import { applyMirroredAppearancePreference } from './lib/theme';
import { routeTree } from './routeTree.gen';

/*
 * The two Paper faces (`FE-04.10`'s dependency decision — see the `--font-sans`
 * comment in `styles.css`). Self-hosted and variable: one woff2 per subset
 * covers weights 200-800, and `unicode-range` means a latin-only page fetches
 * only the latin file. No CDN, no runtime third party, nothing to block on.
 *
 * They are imported HERE and not from `styles.css` for three reasons that all
 * bite at once: `styles.tokens.test.ts` compiles `styles.css` with Tailwind's
 * pure-JS API, whose stylesheet resolver is wired to answer for `tailwindcss`
 * and nothing else; `docs/design/stage2/sync-tokens.mjs` strips exactly one
 * `@import` and would carry any other into a file browsers open directly, where
 * a bare specifier cannot resolve; and `design-tokens-sync.test.ts` asserts the
 * generated copy carries no `@import` at all. Loading a face is an asset
 * concern, and `styles.css` stays the token layer.
 */
import '@fontsource-variable/karla/wght.css';
import '@fontsource-variable/newsreader/wght.css';
import './styles.css';

/**
 * ── `FR-EXP-024` AC4, and the one thing this line cannot do — `FE-04.19` ────
 *
 * *"Given the theme is changed, when the user reloads, then there is no flash
 * of the previous theme."* This call is the whole of it: the mirrored
 * preference is painted onto `<html>` at MODULE SCOPE, before `createRoot`,
 * before React mounts, before the first frame — so the document is already
 * dressed when it is first shown rather than being repainted a round trip
 * later.
 *
 * ⚠ **It is deliberately NOT identity-checked, because at this point in the
 * program identity does not exist yet.** `FE-04.19` keys the mirror to the
 * member who owns it, and the natural expectation is that this line grew a
 * check — it did not, and the reason is structural rather than an omission.
 * Nothing here has asked the network anything: `GET /me` has not been issued,
 * let alone answered, and Better Auth's session cookie is not readable from
 * script. **At the instant the first frame is painted this client does not
 * know, and cannot know, who is looking at it.** A stamp can only be
 * *compared*, and the earliest moment a comparison is possible is when the
 * session resolves — which is `routes/__root.tsx`, one round trip in, where
 * `reconcileMirroredAppearancePreference` does it.
 *
 * So the honest reading of AC4 as shipped: the returning member gets the
 * flash-free reload the criterion promises (and AC4 is written about *"the
 * user"* who changed the theme, so that is the criterion's own subject). A
 * DIFFERENT member on a borrowed browser gets at most one frame of the
 * previous member's theme before the reconciliation takes it away.
 *
 * ⚠ **That one frame was ALREADY one frame before `FE-04.19`, and saying so
 * is the point**, because the residual this item came from claimed otherwise
 * and a reader who believes it will hunt this file for a win that is not
 * here. Measured against the shipped code rather than inferred:
 * `__root.tsx`'s appearance effect depended on `appearanceQuery.data` alone
 * and re-wrote the mirror on every change of it, so a member who SIGNS IN
 * overwrote the previous member's record with their own the moment their
 * preferences resolved — self-healing within the same load, on `main`, with
 * no stamp involved.
 *
 * What was genuinely unbounded is the session that resolves to NOBODY, and
 * that is the case this item closes. The appearance query is gated
 * `enabled: !isSignedOut && !isPending && profile !== null`, so after a
 * sign-out its `data` stays `undefined` forever, its effect returns early,
 * and nothing else in the tree writes `data-theme` — the departed member's
 * private theme stayed painted across every later reload, for every later
 * visitor to that browser. `reconcileMirroredAppearancePreference` is
 * anchored to the SESSION rather than to the appearance query for exactly
 * that reason: `GET /me` resolves for a signed-out visitor, as a 401 that
 * `useSession` reports as `isSignedOut`, where the appearance query never
 * runs at all.
 *
 * Closing even that one frame needs the server to write `data-theme` onto the
 * document before it is sent, because the server knows the session on the very
 * first byte. That is SSR, `apps/web` is a Vite SPA with a static
 * `index.html`, and the gap is minted as `BE-04.1` rather than left implied.
 */
applyMirroredAppearancePreference();

/**
 * Entry point.
 *
 * TanStack Query is here from the start because optimistic update and rollback
 * are the substrate `FR-EXP-031` needs, and hand-rolling them is the single
 * most common way this kind of client goes wrong (§3.F).
 */

/**
 * The route-level boundaries `FE-04.11` ships, installed as router DEFAULTS
 * rather than only on the root route.
 *
 * The distinction is not cosmetic and it is easy to get wrong in the reassuring
 * direction. A React error boundary catches a descendant's throw however many
 * route matches sit between them, so the root's `errorComponent` already covers
 * the whole tree. **Pending state does not bubble that way**: the router
 * resolves `pendingComponent` per route as `route.options.X ?? router.options
 * .defaultX`, so a future child route with a loader and no `pendingComponent`
 * of its own would render nothing at all while it loaded — `FR-EXP-034` AC4's
 * "never left ambiguous", failing silently, on a route nobody thought to wire.
 *
 * Set here because this is the only file that can set them, and `__root.tsx`
 * carries the matching note.
 *
 * ⚠ **Declared BEFORE `queryClient`, and the order is load-bearing.** The
 * cache's `onError` below navigates with this router. `createRouter` needs
 * nothing from the query client, so putting the router first makes the
 * reference an ordinary closure over an already-initialised `const` — rather
 * than a temporal-dead-zone throw *inside an error handler*, which is about
 * the worst place in this file for one. Do not swap them back.
 */
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultPendingComponent: RoutePending,
  defaultPendingMs: ROUTE_PENDING_MS,
  defaultPendingMinMs: ROUTE_PENDING_MIN_MS,
  defaultErrorComponent: RouteError,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * ⚠ **The 401 binding — `FE-06.1`, brief §5.1, `entry-surfaces.md` D2.**
 *
 * One place in the whole client turns "the session is gone" into a redirect,
 * and this is it. A route-level `errorComponent` was the alternative and
 * loses: it fires per route and would race with itself when three queries on
 * one page all 401.
 *
 * Four things about the callback, every one of them load-bearing:
 *
 *  1. **`isUnauthenticated` first, always.** `forbidden` and `not_found` are
 *     not session conditions — a member who may not see a board gets 404 *by
 *     design* (the route is not an existence oracle), and redirecting that
 *     would tell an already-signed-in member to sign in again, forever.
 *     `lib/api.ts` carries the same warning at the other end.
 *  2. **The shell's own probe is excluded BY KEY, not by `throwOnError`.**
 *     D12 proposes marking `/sign-in`'s `GET /me` `throwOnError: false` and
 *     "handling the condition locally". ⚠ **That guard does not work**, and it
 *     was measured rather than assumed: `@tanstack/query-core@5.101.4`
 *     `query.js:323` calls `this.#cache.config.onError?.(error, this)`
 *     unconditionally in the `catch` of `Query#fetch`, immediately after
 *     dispatching the error; `throwOnError` is consumed much later and only
 *     decides whether an *observer rethrows at render* into an error
 *     boundary. So a `['me']` probe that 401s fires this callback whatever
 *     `throwOnError` says — and the callback would navigate to `/sign-in`,
 *     from `/sign-in`, forever. `isSessionQuery` (`lib/session.ts`, the one
 *     file that compares the string `'me'`) is the exclusion that does work.
 *     Not a route-based exclusion: `['me']` is asked for by the shell on
 *     *every* route, so enumerating them would be wrong the moment one is
 *     added.
 *  3. **The consequence of (2) is a hole, and it is plugged deliberately.**
 *     A signed-out visitor to a route whose only query is the excluded probe
 *     gets no bounce from here. `routes/profile.tsx` is that route and
 *     handles `isSignedOut` itself; its docstring says so.
 *  4. **`signInHref(window.location)`, never `location.href`.**
 *     `entry-surfaces.md` D2's own worked example passes `next:
 *     location.href`, which includes the origin — and `safeNextPath` rejects
 *     anything that does not begin with a single `/`, so that spelling would
 *     collapse every redirect to `/` with nothing red anywhere.
 *     `window.location` satisfies `{ pathname, search }` structurally and is
 *     the correct argument; `signInHref` does the encoding.
 *
 * `href`, not `to`: the target carries a querystring (`?next=…`), and `to` is
 * matched against the route-path union rather than parsed.
 *
 * This binding cannot be unit-tested where it lives — importing `main.tsx`
 * boots the application. `apps/web/e2e/FR-PERM-001.spec.ts` is what asserts
 * it, from both sides: a signed-out board URL must bounce here, and
 * `/sign-in` must not.
 */
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!isUnauthenticated(error)) return;
      if (isSessionQuery(query.queryKey)) return;
      /*
       * ⚠ **Already on `/sign-in` means the redirect has already happened.**
       * Added by `D2`, which is this binding's first live caller and the first
       * page in the product where **two** queries 401 together: `GET /boards`
       * and `GET /workspaces` both call `requireUser` unconditionally.
       *
       * Without this guard the second callback reads a `window.location` the
       * first one has already rewritten, so it builds `next` out of the
       * sign-in URL itself and the member lands on
       * `/sign-in?next=%2Fsign-in`. `safeNextPath` maps a `/sign-in` pathname
       * back to `/`, so the *destination* was always right — which is exactly
       * why nothing was red, and why this survived a green browser suite. It
       * was visible only in the address bar.
       *
       * The pathname is the check rather than a "have we navigated" flag: the
       * condition being guarded is a property of where the member is, and it
       * stays correct if a third 401-capable query is ever added to a page.
       */
      if (window.location.pathname === SIGN_IN_PATH) return;
      void router.navigate({ href: signInHref(window.location), replace: true });
    },
  }),
  defaultOptions: {
    queries: {
      // A board is collaborative: data goes stale because someone else changed
      // it, not because time passed. Live updates arrive over the WebSocket, so
      // polling is the fallback rather than the mechanism.
      staleTime: 30_000,
      // Retrying a 401 is a wasted round trip: the session is no more present
      // on the second attempt than the first, and it delays the redirect
      // `FE-06.1` binds to `isUnauthenticated` by one request. Only the two
      // failures that can plausibly differ next time are retried (§3.F).
      retry: shouldRetryRequest,
      refetchOnWindowFocus: true,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

/*
 * The PWA (`docs/notes/pwa.md`). Registers `public/sw.js` on the https origin
 * only. A tapped Android notification arrives from `sw.js` as a message and is
 * followed with a document navigation, exactly as the desktop notification's
 * click is (`DesktopNotificationBridge`) — `lib/service-worker.ts` says why a
 * router navigation would lose the `#comment-` target.
 */
installServiceWorker((href) => {
  window.location.assign(href);
});
